// No need to import OpenAI library anymore
// We'll use fetch to call our serverless function

/**
 * Analyzes a thought and suggests relevant tags based on the thought content
 * 
 * @param {string} thoughtText - The content of the thought to analyze
 * @param {Array<string>} availableTags - List of all available tags in the system
 * @param {Array<string>} existingTags - Tags already associated with this thought (optional)
 * @returns {Promise<Array<string>>} - Promise resolving to an array of suggested tags
 */
export const suggestTagsForThought = async (thoughtText, availableTags, existingTags = []) => {
  try {
    if (!thoughtText) {
      console.error('Missing required thoughtText parameter');
      return [];
    }

    if (!availableTags || !Array.isArray(availableTags)) {
      console.error('availableTags must be an array');
      return [];
    }

    // Ensure existingTags is an array
    const safeExistingTags = Array.isArray(existingTags) ? existingTags : [];
    
    const response = await fetch('/api/openai', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: 'suggestTags',
        thoughtText,
        availableTags,
        existingTags: safeExistingTags,
        model: 'gpt-4o-mini',
        temperature: 0.3,
      }),
    });

    const responseData = await response.json();
    
    if (!response.ok) {
      console.error('API Error:', responseData);
      return [];
    }

    console.log('API response:', responseData);
    
    if (!responseData.choices || !responseData.choices[0] || !responseData.choices[0].message) {
      console.error('Invalid API response format:', responseData);
      return [];
    }
    
    const suggestedTagsText = responseData.choices[0].message.content.trim();
    
    try {
      // Preprocess the response to fix common JSON parsing issues
      let processedResponse = suggestedTagsText
        // Replace backticks with double quotes
        .replace(/`/g, '"')
        // Replace single quotes with double quotes
        .replace(/'/g, '"')
        // Extract JSON array if it's wrapped in markdown code blocks
        .replace(/```json\s*([\s\S]*)\s*```/g, '$1')
        .replace(/```\s*([\s\S]*)\s*```/g, '$1')
        .trim();
      
      // Make sure the response starts with [ and ends with ]
      if (!processedResponse.startsWith('[')) {
        const startBracket = processedResponse.indexOf('[');
        if (startBracket >= 0) {
          processedResponse = processedResponse.substring(startBracket);
        }
      }
      if (!processedResponse.endsWith(']')) {
        const endBracket = processedResponse.lastIndexOf(']');
        if (endBracket >= 0) {
          processedResponse = processedResponse.substring(0, endBracket + 1);
        }
      }
      
      console.log('Processed response for parsing:', processedResponse);
      
      // Try to parse the response as JSON
      const suggestedTags = JSON.parse(processedResponse);
      return Array.isArray(suggestedTags) ? suggestedTags : [];
    } catch (error) {
      console.error('Failed to parse API response as JSON:', error);
      // If JSON parsing fails, try to extract tags using regex
      const matches = suggestedTagsText.match(/["'`]([^"'`]+)["'`]/g);
      return matches ? matches.map(m => m.replace(/["'`]/g, '')) : [];
    }
  } catch (error) {
    console.error('Error suggesting tags:', error);
    return [];
  }
};

/**
 * Suggests new conceptual themes for tagging, focusing on new tag ideas 
 * not already in the user's tagging system
 * 
 * @param {string} thoughtText - The content of the thought to analyze
 * @param {Array<string>} availableTags - List of all available tags in the system
 * @param {Array<string>} existingTags - Tags already associated with this thought (optional)
 * @returns {Promise<Array<string>>} - Promise resolving to an array of suggested new themes
 */
export const suggestNewThemes = async (thoughtText, availableTags, existingTags = []) => {
  try {
    if (!thoughtText) {
      console.error('Missing required thoughtText parameter');
      return [];
    }

    if (!availableTags || !Array.isArray(availableTags)) {
      console.error('availableTags must be an array');
      return [];
    }

    // Ensure existingTags is an array
    const safeExistingTags = Array.isArray(existingTags) ? existingTags : [];
    
    console.log('Calling OpenAI API for new themes with:', {
      type: 'suggestNewThemes',
      thoughtText: thoughtText.substring(0, 50) + '...',
      availableTags: availableTags.length,
      existingTags: safeExistingTags.length
    });

    const response = await fetch('/api/openai', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: 'suggestNewThemes',
        thoughtText,
        availableTags,
        existingTags: safeExistingTags,
        model: 'gpt-4o-mini',
        temperature: 0.5,
      }),
    });

    const responseData = await response.json();
    
    if (!response.ok) {
      console.error('API Error:', responseData);
      return [];
    }

    console.log('API response (new themes):', responseData);
    
    if (!responseData.choices || !responseData.choices[0] || !responseData.choices[0].message) {
      console.error('Invalid API response format:', responseData);
      return [];
    }
    
    const suggestedTagsText = responseData.choices[0].message.content.trim();
    
    try {
      // Preprocess the response to fix common JSON parsing issues
      let processedResponse = suggestedTagsText
        // Replace backticks with double quotes
        .replace(/`/g, '"')
        // Replace single quotes with double quotes
        .replace(/'/g, '"')
        // Extract JSON array if it's wrapped in markdown code blocks
        .replace(/```json\s*([\s\S]*)\s*```/g, '$1')
        .replace(/```\s*([\s\S]*)\s*```/g, '$1')
        .trim();
      
      // Make sure the response starts with [ and ends with ]
      if (!processedResponse.startsWith('[')) {
        const startBracket = processedResponse.indexOf('[');
        if (startBracket >= 0) {
          processedResponse = processedResponse.substring(startBracket);
        }
      }
      if (!processedResponse.endsWith(']')) {
        const endBracket = processedResponse.lastIndexOf(']');
        if (endBracket >= 0) {
          processedResponse = processedResponse.substring(0, endBracket + 1);
        }
      }
      
      console.log('Processed response for parsing:', processedResponse);
      
      // Try to parse the response as JSON
      const suggestedTags = JSON.parse(processedResponse);
      return Array.isArray(suggestedTags) ? suggestedTags : [];
    } catch (error) {
      console.error('Failed to parse API response as JSON:', error);
      // If JSON parsing fails, try to extract tags using regex
      const matches = suggestedTagsText.match(/["'`]([^"'`]+)["'`]/g);
      return matches ? matches.map(m => m.replace(/["'`]/g, '')) : [];
    }
  } catch (error) {
    console.error('Error suggesting new themes:', error);
    return [];
  }
};

/**
 * Generates an AI summary of thoughts over a given timeframe
 * 
 * @param {Array<Object>} thoughts - Array of thought objects to analyze
 * @param {number} timeframe - The timeframe in days that the thoughts cover
 * @returns {Promise<string>} - Promise resolving to a summary of insights
 */
export const generateThoughtSummary = async (thoughts, timeframe) => {
  try {
    if (!thoughts || !Array.isArray(thoughts) || thoughts.length === 0) {
      console.log('No thoughts provided for summary generation');
      return null;
    }

    // Extract only the thought content and ensure no null/undefined values
    const thoughtTexts = thoughts
      .filter(thought => thought && thought.thought && typeof thought.thought === 'string')
      .map(thought => thought.thought.trim())
      .filter(text => text !== '');
    
    // Return early if no valid thought texts remain
    if (thoughtTexts.length === 0) {
      console.log('No valid thought texts found for summary generation');
      return null;
    }

    console.log(`Generating summary for ${thoughtTexts.length} thoughts over ${timeframe} days`);
    console.log(thoughtTexts[0]);

    const response = await fetch('/api/openai', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: 'generateSummary',
        thoughtTexts,
        timeframe,
        model: 'gpt-4o-mini',
        temperature: 0.7,
      }),
    });

    const responseData = await response.json();
    
    if (!response.ok) {
      console.error('API Error:', responseData);
      return null;
    }

    if (!responseData.choices || !responseData.choices[0] || !responseData.choices[0].message) {
      console.error('Invalid API response format:', responseData);
      return null;
    }
    
    return responseData.choices[0].message.content.trim();
  } catch (error) {
    console.error('Error generating thought summary:', error);
    return null;
  }
};

/**
 * Performs a deep psychological analysis of a thought
 * 
 * @param {string} thoughtText - The content of the thought to analyze
 * @returns {Promise<string>} - Promise resolving to the analysis and transcendence guidance
 */
export const getDeepAnalysis = async (thoughtText) => {
  try {
    if (!thoughtText) {
      console.error('Missing required thoughtText parameter');
      return null;
    }

    const response = await fetch('/.netlify/functions/openai', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: 'custom',
        prompt: `As a psychologist, analyze this thought deeply:

"${thoughtText}"

Provide a concise response with these sections:
1. Core Insight: Key psychological patterns and dynamics
2. Current Stage: Where this indicates you are in your journey
3. Next Step: What is the next step of transcendence for this thought and mindset?

Do not use markdown, just write plain text.

Keep each section to 4-5 sentences.`,
        model: 'gpt-4o-mini',
        temperature: 1,
      }),
    });

    const responseData = await response.json();
    
    if (!response.ok) {
      console.error('API Error:', responseData);
      return null;
    }

    if (!responseData.choices || !responseData.choices[0] || !responseData.choices[0].message) {
      console.error('Invalid API response format:', responseData);
      return null;
    }
    
    return responseData.choices[0].message.content.trim();
  } catch (error) {
    console.error('Error getting deep analysis:', error);
    return null;
  }
}; 